import { createTheme, alpha, type Theme } from '@mui/material/styles';

export type ThemeMode = 'light' | 'dark' | 'high-contrast';

const GREEN = {
  50: '#E6F8F1',
  100: '#C2EFDD',
  300: '#5DD9A8',
  500: '#00B881',
  600: '#009A6B',
  700: '#007A55',
  900: '#003D2A',
};

const BLUE = {
  50: '#EAF1FE',
  100: '#C9D8FB',
  300: '#6E8DEF',
  500: '#2256E0',
  600: '#1A45B7',
  700: '#13348A',
};

const NEUTRAL = {
  light: {
    bg: '#F7F8FA',
    paper: '#FFFFFF',
    paperElev: '#FFFFFF',
    sidebar: '#FFFFFF',
    border: '#EAECEF',
    borderStrong: '#D1D5DB',
    divider: '#F1F3F5',
    text: '#1A202C',
    textSec: '#6B7280',
    textDis: '#9CA3AF',
  },
  dark: {
    bg: '#15161B',
    paper: '#1E2026',
    paperElev: '#262932',
    sidebar: '#1E2026',
    border: '#2A2D33',
    borderStrong: '#3A3F47',
    divider: '#23262C',
    text: '#E8EAED',
    textSec: '#9AA0A6',
    textDis: '#5F6368',
  },
  hc: {
    bg: '#000000',
    paper: '#000000',
    paperElev: '#0A0A0A',
    sidebar: '#000000',
    border: '#FFFFFF',
    borderStrong: '#FFFFFF',
    divider: '#FFFFFF',
    text: '#FFFFFF',
    textSec: '#FFFFFF',
    textDis: '#A8A8A8',
  },
};

const SEMANTIC = {
  light: {
    info: { bg: '#E5F0FA', fg: '#0D4A85', border: '#9BC6E8' },
    success: { bg: '#E3F2E8', fg: '#1F5C32', border: '#9CCAA9' },
    warning: { bg: '#FBF0DA', fg: '#7A4E0F', border: '#E2BF7E' },
    error: { bg: '#FBE7E5', fg: '#8C2A24', border: '#E2A19D' },
  },
  dark: {
    info: { bg: '#0F2A40', fg: '#9CC9F0', border: '#1E4870' },
    success: { bg: '#15301E', fg: '#9CD4AE', border: '#27553A' },
    warning: { bg: '#3A2810', fg: '#E8C588', border: '#634415' },
    error: { bg: '#3D1714', fg: '#F0A8A2', border: '#6C2924' },
  },
  hc: {
    info: { bg: '#001A33', fg: '#9ECCFF', border: '#7FB9FF' },
    success: { bg: '#002B14', fg: '#9CF5BB', border: '#5FE08D' },
    warning: { bg: '#2B1C00', fg: '#FFD78A', border: '#FFB84D' },
    error: { bg: '#330000', fg: '#FFB3AC', border: '#FF6A60' },
  },
};

const SEVERITY = {
  light: {
    info: { bg: '#E5F0FA', fg: '#0D4A85', dot: '#3A82C9' },
    low: { bg: '#FBF0DA', fg: '#7A4E0F', dot: '#D89C2D' },
    high: { bg: '#FBE7DC', fg: '#8C3F1A', dot: '#E07334' },
    critical: { bg: '#F4D6D2', fg: '#7A1812', dot: '#B6271E' },
  },
  dark: {
    info: { bg: '#0F2A40', fg: '#9CC9F0', dot: '#5CA0DC' },
    low: { bg: '#3A2810', fg: '#E8C588', dot: '#D8A24A' },
    high: { bg: '#3F2415', fg: '#F0B58F', dot: '#E08456' },
    critical: { bg: '#3D1714', fg: '#F0A8A2', dot: '#D14B40' },
  },
  hc: {
    info: { bg: '#001A33', fg: '#9ECCFF', dot: '#9ECCFF' },
    low: { bg: '#2B1C00', fg: '#FFD78A', dot: '#FFD78A' },
    high: { bg: '#3A1C00', fg: '#FFB388', dot: '#FFB388' },
    critical: { bg: '#330000', fg: '#FFB3AC', dot: '#FF8A80' },
  },
};

declare module '@mui/material/styles' {
  interface Palette {
    severity: {
      info: { bg: string; fg: string; dot: string };
      low: { bg: string; fg: string; dot: string };
      high: { bg: string; fg: string; dot: string };
      critical: { bg: string; fg: string; dot: string };
    };
    surface: {
      sidebar: string;
      paperElev: string;
      borderStrong: string;
    };
  }
  interface PaletteOptions {
    severity?: Palette['severity'];
    surface?: Palette['surface'];
  }
  interface Components {
    MuiDataGrid?: {
      styleOverrides?: { root?: Record<string, unknown> };
    };
  }
}

const FONT_STACK =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
const MONO_STACK =
  '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace';

const baseTypography = {
  fontFamily: FONT_STACK,
  fontSize: 14,
  htmlFontSize: 16,
  h1: { fontSize: '2.25rem', fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.025em' },
  h2: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.25, letterSpacing: '-0.016em' },
  h3: { fontSize: '1.375rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.013em' },
  h4: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.35, letterSpacing: '-0.008em' },
  h5: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 },
  h6: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.45 },
  body1: { fontSize: '0.875rem', lineHeight: 1.5 },
  body2: { fontSize: '0.8125rem', lineHeight: 1.45 },
  caption: { fontSize: '0.75rem', lineHeight: 1.4 },
  overline: {
    fontSize: '0.6875rem',
    lineHeight: 1.4,
    letterSpacing: '0.05em',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
  },
  button: {
    fontSize: '0.8438rem',
    fontWeight: 500,
    letterSpacing: '0.005em',
    textTransform: 'none' as const,
  },
  subtitle1: { fontSize: '0.9375rem', fontWeight: 500, lineHeight: 1.5 },
  subtitle2: { fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.5 },
};

const softShadows = [
  'none',
  '0 1px 2px rgba(20,30,40,0.06)',
  '0 2px 6px rgba(20,30,40,0.06), 0 1px 2px rgba(20,30,40,0.04)',
  '0 4px 10px rgba(20,30,40,0.06), 0 2px 4px rgba(20,30,40,0.05)',
  '0 12px 28px rgba(20,30,40,0.08), 0 4px 8px rgba(20,30,40,0.05)',
  ...new Array(20).fill('0 16px 40px rgba(20,30,40,0.12)'),
] as Theme['shadows'];

const heavyShadows = [
  'none',
  '0 1px 2px rgba(0,0,0,0.4)',
  '0 2px 8px rgba(0,0,0,0.45), 0 1px 2px rgba(0,0,0,0.3)',
  '0 4px 12px rgba(0,0,0,0.5), 0 2px 4px rgba(0,0,0,0.4)',
  '0 12px 32px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.35)',
  ...new Array(20).fill('0 20px 50px rgba(0,0,0,0.6)'),
] as Theme['shadows'];

const noShadows = new Array(25).fill('none') as Theme['shadows'];

function lightTheme(): Theme {
  const N = NEUTRAL.light;
  const S = SEMANTIC.light;
  const primaryMain = BLUE[500];
  const primaryDark = BLUE[700];
  const primaryLight = BLUE[100];
  const secondaryMain = GREEN[500];
  const errorContrast = '#FFFFFF';
  return createTheme({
    palette: {
      mode: 'light',
      primary: { main: primaryMain, dark: primaryDark, light: primaryLight, contrastText: '#FFFFFF' },
      secondary: { main: secondaryMain, dark: GREEN[700], light: GREEN[100], contrastText: '#FFFFFF' },
      background: { default: N.bg, paper: N.paper },
      text: { primary: N.text, secondary: N.textSec, disabled: N.textDis },
      divider: N.divider,
      info: { main: S.info.fg, light: S.info.bg, dark: S.info.fg, contrastText: '#FFFFFF' },
      success: { main: S.success.fg, light: S.success.bg, dark: S.success.fg, contrastText: '#FFFFFF' },
      warning: { main: S.warning.fg, light: S.warning.bg, dark: S.warning.fg, contrastText: '#FFFFFF' },
      error: { main: S.error.fg, light: S.error.bg, dark: S.error.fg, contrastText: errorContrast },
      severity: SEVERITY.light,
      surface: { sidebar: N.sidebar, paperElev: N.paperElev, borderStrong: N.borderStrong },
    },
    typography: baseTypography,
    shape: { borderRadius: 8 },
    shadows: softShadows,
    components: sharedComponents({ mode: 'light', N, S, primaryMain, primaryDark, errorMain: S.error.fg, errorContrast }),
  });
}

function darkTheme(): Theme {
  const N = NEUTRAL.dark;
  const S = SEMANTIC.dark;
  const primaryMain = BLUE[300];
  const primaryDark = BLUE[500];
  const primaryLight = BLUE[100];
  const secondaryMain = GREEN[300];
  const errorContrast = '#0B1013';
  return createTheme({
    palette: {
      mode: 'dark',
      primary: { main: primaryMain, dark: primaryDark, light: primaryLight, contrastText: '#0B1013' },
      secondary: { main: secondaryMain, dark: GREEN[500], light: GREEN[100], contrastText: '#0B1013' },
      background: { default: N.bg, paper: N.paper },
      text: { primary: N.text, secondary: N.textSec, disabled: N.textDis },
      divider: N.divider,
      info: { main: S.info.fg, light: S.info.bg, dark: S.info.fg, contrastText: '#0B1013' },
      success: { main: S.success.fg, light: S.success.bg, dark: S.success.fg, contrastText: '#0B1013' },
      warning: { main: S.warning.fg, light: S.warning.bg, dark: S.warning.fg, contrastText: '#0B1013' },
      error: { main: S.error.fg, light: S.error.bg, dark: S.error.fg, contrastText: errorContrast },
      severity: SEVERITY.dark,
      surface: { sidebar: N.sidebar, paperElev: N.paperElev, borderStrong: N.borderStrong },
    },
    typography: baseTypography,
    shape: { borderRadius: 8 },
    shadows: heavyShadows,
    components: sharedComponents({ mode: 'dark', N, S, primaryMain, primaryDark, errorMain: S.error.fg, errorContrast }),
  });
}

function highContrastTheme(): Theme {
  const N = NEUTRAL.hc;
  const S = SEMANTIC.hc;
  // WN-076 wysoki kontrast: żółty primary + cyan secondary (oryginalna paleta dostępności,
  // niezależna od CoinGecko-style green/blue z light/dark — żółty na czarnym daje
  // największy kontrast i jest standardem akcesybilności WCAG AAA).
  const primaryMain = '#FFEB00';
  const primaryDark = '#CCBC00';
  const primaryLight = '#FFF480';
  const secondaryMain = '#00FFFF';
  const errorContrast = '#000000';
  // HC destruktywny: jaśniejszy czerwony (border SEMANTIC) niż fg pastel — daje wizualne
  // odróżnienie "Usuń"/"Wyłącz" od neutralnych przycisków, zachowując kontrast 7:1 na czarnym.
  const errorMain = '#FF6A60';
  const focusRing = '0 0 0 3px #FFD400, 0 0 0 5px #000000';
  return createTheme({
    palette: {
      mode: 'dark',
      primary: { main: primaryMain, dark: primaryDark, light: primaryLight, contrastText: '#000000' },
      secondary: { main: secondaryMain, dark: '#00CCCC', light: '#80FFFF', contrastText: '#000000' },
      background: { default: N.bg, paper: N.paper },
      text: { primary: N.text, secondary: N.textSec, disabled: N.textDis },
      divider: N.divider,
      info: { main: S.info.fg, light: S.info.bg, dark: S.info.fg, contrastText: '#000000' },
      success: { main: S.success.fg, light: S.success.bg, dark: S.success.fg, contrastText: '#000000' },
      warning: { main: S.warning.fg, light: S.warning.bg, dark: S.warning.fg, contrastText: '#000000' },
      error: { main: errorMain, light: S.error.bg, dark: errorMain, contrastText: errorContrast },
      severity: SEVERITY.hc,
      surface: { sidebar: N.sidebar, paperElev: N.paperElev, borderStrong: N.borderStrong },
    },
    typography: { ...baseTypography, fontSize: 15 },
    shape: { borderRadius: 4 },
    shadows: noShadows,
    components: sharedComponents({ mode: 'hc', N, S, primaryMain, primaryDark, errorMain, errorContrast, focusRing }),
  });
}

type SharedComponentsArgs = {
  mode: 'light' | 'dark' | 'hc';
  N: typeof NEUTRAL.light;
  S: typeof SEMANTIC.light;
  primaryMain: string;
  primaryDark: string;
  errorMain: string;
  errorContrast: string;
  focusRing?: string;
};

// Disabled style dla `variant="contained"` (primary + error) — neutralne wyszarzenie zamiast
// gradientu/koloru bazowego. Tło wyraźnie odróżnia się od enabled, tekst jest czytelny ale
// stłumiony (kontrast WCAG AA: 4.5:1 w light, 3:1 jako "disabled" jest dopuszczalne wg 1.4.3
// note: disabled inactive UI components nie wymagają kontrastu, ale celujemy w czytelność).
function disabledContainedStyles(mode: 'light' | 'dark' | 'hc', N: typeof NEUTRAL.light) {
  if (mode === 'light') {
    return {
      background: 'none',
      backgroundColor: '#E5E7EB',
      color: '#6B7280',
      boxShadow: 'none',
      filter: 'none',
    };
  }
  if (mode === 'hc') {
    return {
      background: 'none',
      backgroundColor: '#2A2A2A',
      color: '#A8A8A8',
      boxShadow: 'none',
      filter: 'none',
      border: `2px solid ${N.borderStrong}`,
    };
  }
  // dark
  return {
    background: 'none',
    backgroundColor: '#2A2D33',
    color: '#6B7280',
    boxShadow: 'none',
    filter: 'none',
  };
}

function sharedComponents({
  mode,
  N,
  S,
  primaryMain,
  primaryDark,
  errorMain,
  errorContrast,
  focusRing,
}: SharedComponentsArgs) {
  const isHC = mode === 'hc';
  const minHit = isHC ? 48 : 36;
  const borderWidth = isHC ? 2 : 1;
  const radiusBase = isHC ? 4 : 8;
  const radiusLarge = isHC ? 4 : 12;
  const ring = focusRing ?? `0 0 0 2px ${alpha(primaryMain, 0.25)}, 0 0 0 3px ${primaryMain}`;
  const scrollbarThumb = mode === 'light' ? '#cbd5cd' : isHC ? '#FFFFFF' : '#334155';
  const scrollbarThumbHover = mode === 'light' ? '#94a3a8' : isHC ? '#FFD400' : '#475569';

  return {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: N.bg, color: N.text, fontFamily: FONT_STACK },
        '.mono, code, kbd, samp, pre': { fontFamily: MONO_STACK },
        a: { color: primaryMain, textUnderlineOffset: 2 },
        '*:focus-visible': {
          outline: 'none',
          boxShadow: ring,
          borderRadius: radiusBase,
        },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-track': { background: 'transparent' },
        '*::-webkit-scrollbar-thumb': {
          background: scrollbarThumb,
          borderRadius: 6,
          ...(isHC && { border: '2px solid #000000' }),
        },
        '*::-webkit-scrollbar-thumb:hover': { background: scrollbarThumbHover },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: minHit,
          paddingInline: 14,
          borderRadius: radiusBase,
          borderWidth,
          fontWeight: 500,
          letterSpacing: '0.005em',
          transition:
            'background 180ms ease, box-shadow 180ms ease, border-color 180ms ease, filter 180ms ease, color 180ms ease',
        },

        // === CONTAINED ===
        // Primary CTA: gradient + soft shadow (light/dark) lub solid + brightness (HC).
        // Wszystkie kolorystyczne style są scope'owane na :not(.Mui-disabled), żeby disabled
        // state używał theme.palette.action.disabled* (czytelne wyszarzenie zamiast gradientu).
        containedPrimary: isHC
          ? {
              boxShadow: 'none',
              '&:hover:not(.Mui-disabled)': { backgroundColor: primaryMain, filter: 'brightness(1.1)' },
              '&.Mui-disabled': disabledContainedStyles(mode, N),
            }
          : {
              '&:not(.Mui-disabled)': {
                background: `linear-gradient(135deg, ${primaryMain} 0%, ${primaryDark} 100%)`,
                boxShadow: `0 1px 2px ${alpha(primaryMain, 0.20)}`,
              },
              '&:hover:not(.Mui-disabled)': {
                background: `linear-gradient(135deg, ${primaryDark} 0%, ${primaryMain} 100%)`,
                boxShadow: `0 4px 14px ${alpha(primaryMain, 0.36)}`,
                filter: 'brightness(1.04)',
              },
              '&.Mui-disabled': disabledContainedStyles(mode, N),
            },
        // Destruktywny CTA: solidny czerwony, ten sam wzorzec shadow/hover co primary.
        containedError: {
          '&:not(.Mui-disabled)': {
            backgroundColor: errorMain,
            color: errorContrast,
            boxShadow: isHC ? 'none' : `0 1px 2px ${alpha(errorMain, 0.20)}`,
          },
          '&:hover:not(.Mui-disabled)': {
            backgroundColor: errorMain,
            filter: 'brightness(1.08)',
            boxShadow: isHC ? 'none' : `0 4px 14px ${alpha(errorMain, 0.32)}`,
          },
          '&.Mui-disabled': disabledContainedStyles(mode, N),
        },

        // === OUTLINED ===
        outlined: { borderWidth },
        outlinedPrimary: {
          borderColor: alpha(primaryMain, isHC ? 1 : 0.5),
          ':hover': {
            borderColor: primaryMain,
            backgroundColor: alpha(primaryMain, mode === 'light' ? 0.05 : 0.12),
          },
        },
        outlinedError: {
          borderColor: alpha(errorMain, isHC ? 1 : 0.5),
          color: errorMain,
          ':hover': {
            borderColor: errorMain,
            backgroundColor: alpha(errorMain, mode === 'light' ? 0.05 : 0.12),
          },
        },
        outlinedInherit: {
          borderColor: N.borderStrong,
          color: N.text,
          ':hover': {
            borderColor: primaryMain,
            backgroundColor: alpha(primaryMain, mode === 'light' ? 0.04 : 0.10),
          },
        },

        // === TEXT ===
        textPrimary: {
          ':hover': { backgroundColor: alpha(primaryMain, mode === 'light' ? 0.06 : 0.14) },
        },
        textError: {
          color: errorMain,
          ':hover': { backgroundColor: alpha(errorMain, mode === 'light' ? 0.06 : 0.14) },
        },
        textInherit: {
          color: N.textSec,
          ':hover': {
            color: N.text,
            backgroundColor: alpha(primaryMain, mode === 'light' ? 0.05 : 0.10),
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: minHit,
          minHeight: minHit,
          borderRadius: radiusBase,
          ':hover': { backgroundColor: alpha(primaryMain, mode === 'light' ? 0.08 : 0.16) },
        },
        // Semantic hover: ikony "destruktywne" / "ostrzegawcze" mają hover bg w tym kolorze
        // — zamiast wszystkich w primary alpha — utrzymuje sygnał kolorystyczny.
        colorError: {
          color: errorMain,
          ':hover': { backgroundColor: alpha(errorMain, mode === 'light' ? 0.08 : 0.16) },
        },
        colorWarning: {
          ':hover': { backgroundColor: alpha(S.warning.fg, mode === 'light' ? 0.08 : 0.16) },
        },
        colorSuccess: {
          ':hover': { backgroundColor: alpha(S.success.fg, mode === 'light' ? 0.08 : 0.16) },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none', backgroundColor: N.paper },
      },
    },
    MuiCard: {
      defaultProps: { variant: 'outlined' as const },
      styleOverrides: {
        root: {
          borderColor: N.border,
          borderWidth,
          borderRadius: radiusLarge,
          backgroundColor: N.paper,
          transition: 'border-color 180ms ease, box-shadow 180ms ease',
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          borderRadius: radiusLarge,
          ':hover': { backgroundColor: alpha(primaryMain, 0.04) },
        },
      },
    },
    MuiAppBar: {
      defaultProps: { color: 'default' as const, elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: isHC ? N.paper : alpha(N.paper, 0.92),
          backdropFilter: isHC ? 'none' : 'saturate(180%) blur(8px)',
          color: N.text,
          borderBottom: `${borderWidth}px solid ${N.border}`,
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: N.sidebar,
          borderRight: `${borderWidth}px solid ${N.border}`,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: radiusBase,
          minHeight: minHit,
          paddingTop: 8,
          paddingBottom: 8,
          paddingInline: 12,
          color: N.text,
          ':hover': { backgroundColor: alpha(primaryMain, mode === 'light' ? 0.06 : 0.12) },
          '&.Mui-selected': {
            backgroundColor: alpha(primaryMain, mode === 'light' ? 0.10 : 0.20),
            color: primaryMain,
            fontWeight: 600,
            '&:hover': { backgroundColor: alpha(primaryMain, mode === 'light' ? 0.14 : 0.26) },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: { root: { minWidth: 36, color: 'inherit' } },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'light' ? '#F7F8FA' : isHC ? '#0A0A0A' : '#1B1D23',
          '& .MuiTableCell-root': {
            fontWeight: 600,
            color: N.textSec,
            textTransform: 'uppercase',
            fontSize: '0.6875rem',
            letterSpacing: '0.06em',
            borderBottom: `${borderWidth}px solid ${N.border}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: N.divider,
          paddingBlock: 12,
          fontSize: '0.875rem',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 120ms ease',
          '&:hover:not(.MuiTableRow-head)': {
            backgroundColor: mode === 'light' ? '#FAFBFC' : isHC ? '#1A1A1A' : '#23262C',
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `${borderWidth}px solid ${N.border}`,
          borderRadius: radiusBase,
          fontFamily: FONT_STACK,
          fontSize: '0.875rem',
          backgroundColor: N.paper,
          '--DataGrid-rowBorderColor': N.divider,
          '--DataGrid-containerBackground': mode === 'light' ? '#F7F8FA' : isHC ? '#0A0A0A' : '#1B1D23',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: mode === 'light' ? '#F7F8FA' : isHC ? '#0A0A0A' : '#1B1D23',
            borderBottom: `${borderWidth}px solid ${N.border}`,
          },
          '& .MuiDataGrid-columnHeader': {
            fontSize: '0.6875rem',
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: N.textSec,
          },
          '& .MuiDataGrid-columnSeparator': { display: 'none' },
          '& .MuiDataGrid-cell': {
            borderBottomColor: N.divider,
            color: N.text,
            '&:focus, &:focus-within': { outline: 'none' },
          },
          '& .MuiDataGrid-row': {
            transition: 'background-color 120ms ease',
            '&:hover': {
              backgroundColor: mode === 'light' ? '#FAFBFC' : isHC ? '#1A1A1A' : '#23262C',
            },
            '&.Mui-selected': {
              backgroundColor: alpha(primaryMain, mode === 'light' ? 0.08 : 0.16),
              '&:hover': { backgroundColor: alpha(primaryMain, mode === 'light' ? 0.12 : 0.22) },
            },
          },
          '& .MuiDataGrid-cell--textRight, & .MuiDataGrid-columnHeader--alignRight, & .MuiDataGrid-cell[data-numeric="true"]': {
            fontFamily: MONO_STACK,
            fontVariantNumeric: 'tabular-nums',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: `${borderWidth}px solid ${N.border}`,
            backgroundColor: mode === 'light' ? '#FBFCFD' : N.paper,
          },
          '& .MuiDataGrid-overlay': { backgroundColor: 'transparent' },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radiusBase,
          backgroundColor: N.paper,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: N.border,
            borderWidth,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: N.borderStrong },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: primaryMain,
            borderWidth: isHC ? 2 : 1.5,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: { padding: '8px 12px' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 500,
          fontSize: '0.75rem',
          height: 24,
          ...(isHC && { borderWidth: 2 }),
        },
        outlined: { borderWidth },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: radiusBase,
          border: `${borderWidth}px solid transparent`,
          fontSize: '0.8125rem',
          lineHeight: 1.5,
        },
        standardInfo: {
          backgroundColor: S.info.bg,
          color: S.info.fg,
          borderColor: S.info.border,
          '& .MuiAlert-icon': { color: S.info.fg },
        },
        standardSuccess: {
          backgroundColor: S.success.bg,
          color: S.success.fg,
          borderColor: S.success.border,
          '& .MuiAlert-icon': { color: S.success.fg },
        },
        standardWarning: {
          backgroundColor: S.warning.bg,
          color: S.warning.fg,
          borderColor: S.warning.border,
          '& .MuiAlert-icon': { color: S.warning.fg },
        },
        standardError: {
          backgroundColor: S.error.bg,
          color: S.error.fg,
          borderColor: S.error.border,
          '& .MuiAlert-icon': { color: S.error.fg },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.75rem',
          borderRadius: radiusBase,
          backgroundColor: mode === 'light' ? N.text : isHC ? '#000000' : '#0F1623',
          color: mode === 'light' ? N.paper : N.text,
          border: isHC ? `${borderWidth}px solid ${N.border}` : 'none',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: N.divider },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 2,
          backgroundColor: primaryMain,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.84rem',
          minHeight: minHit + 8,
          paddingInline: 14,
          color: N.textSec,
          '&.Mui-selected': { color: primaryMain, fontWeight: 600 },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: '0.8rem',
          ...(isHC && { border: `${borderWidth}px solid ${N.border}` }),
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: { fontWeight: 700, fontSize: '0.625rem' },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          ':focus-visible': { boxShadow: ring },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          ':focus-visible': { boxShadow: ring },
        },
      },
    },
  };
}

export function createAppTheme(mode: ThemeMode): Theme {
  switch (mode) {
    case 'light':
      return lightTheme();
    case 'dark':
      return darkTheme();
    case 'high-contrast':
      return highContrastTheme();
  }
}

export const themePalette = {
  GREEN,
  BLUE,
  NEUTRAL,
  SEMANTIC,
  SEVERITY,
  FONT_STACK,
  MONO_STACK,
};
